<template>
	<v-container id='container' class='fill-height' fluid>
		<v-row align='center' justify='center'>
			<v-col>
				<v-card :loading='loading' :disabled='loading'>
					<v-card-title>
						Results list
					</v-card-title>
					<v-card-text>
						<v-row>
							<v-col lg='2' md='6'>
								<date-picker
									v-model='date'
									hide-details
									dense
									label='Date'
									@change='onDateChange'
								></date-picker>
							</v-col>
						</v-row>
						<v-row>
							<v-slide-y-reverse-transition duration='150' mode='out-in'>
								<v-col
									v-if='currentlyEditingResult'
									:key='currentlyEditingResult.sortition.id'
									lg='8'
								>
									<result-editor
										:date='date'
										:result='currentlyEditingResult'
										@close='currentlyEditingResult = null'
										@save='saveResult($event.result)'
									>
									</result-editor>
								</v-col>
							</v-slide-y-reverse-transition>
						</v-row>
						<v-row>
							<v-col>
								<v-data-table
									dense
									:headers='columnHeaders'
									:items='results'
									:items-per-page='20'
								>
									<template #header.actions>
										<font-awesome-icon icon='cog'></font-awesome-icon>
									</template>
									<template #item.actions='{ item }'>
										<v-btn icon @click='currentlyEditingResult = item'>
											<font-awesome-icon icon='pencil-alt'></font-awesome-icon>
										</v-btn>
										<v-btn icon @click='currentlyDestroyingResult = item'>
											<font-awesome-icon icon='eraser'></font-awesome-icon>
										</v-btn>
									</template>
								</v-data-table>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
		<result-delete-dialog
			:value='Boolean(currentlyDestroyingResult)'
			:date='date'
			:result='currentlyDestroyingResult'
			@close='currentlyDestroyingResult = null'
			@delete='clearResult'
		>
		</result-delete-dialog>
	</v-container>
</template>

<script>
import DatePicker from '../../../components/inputs/DatePicker.vue';
import ContentLoader from '../../../mixins/ContentLoader.js';
import { fetchResults, updateResults } from '@/api/result.api.js';
import ResultEditor from '../../../components/result/ResultEditor.vue';
import ResultDeleteDialog from '../../../components/result/ResultDeleteDialog.vue';

export default {
	name: 'ResultIndex',
	components: {
		ResultDeleteDialog,
		ResultEditor,
		DatePicker
	},
	mixins: [ContentLoader],
	data() {
		return {
			date: '',
			results: [],
			currentlyEditingResult: null,
			currentlyDestroyingResult: null,
			requiredFields: {
				requireFirst: false,
				requireSecond: false,
				requireThird: false,
				requirePickOne: false,
				requirePickTwo: false,
				requireCashThree: false,
				requirePickFour: false,
				requirePickFive: false
			}
		};
	},
	async created() {
		this.$startLoading();
		try {
			await this.getResults();
		} catch (e) {
			console.error(e);
		} finally {
			this.$finishedLoading();
		}
	},
	computed: {
		columnHeaders() {
			const headers = [
				{
					text: 'Sortition',
					value: 'sortition.abbreviation',
					sortable: false
				}
			];

			if (this.requiredFields.requireFirst) {
				headers.push({
					text: '1st',
					value: 'first',
					sortable: false
				});
			}
			if (this.requiredFields.requireSecond) {
				headers.push({
					text: '2nd',
					value: 'second',
					sortable: false
				});
			}
			if (this.requiredFields.requireThird) {
				headers.push({
					text: '3rd',
					value: 'third',
					sortable: false
				});
			}
			if (this.requiredFields.requirePickOne) {
				headers.push({
					text: 'Pick One',
					value: 'pickOne',
					sortable: false
				});
			}
			if (this.requiredFields.requirePickTwo) {
				headers.push({
					text: 'Pick Two',
					value: 'pickTwo',
					sortable: false
				});
			}
			if (this.requiredFields.requireCashThree) {
				headers.push({
					text: 'Cash 3',
					value: 'cashThree',
					sortable: false
				});
			}
			if (this.requiredFields.requirePickFour) {
				headers.push({
					text: 'Pick 4',
					value: 'pickFour',
					sortable: false
				});
			}
			if (this.requiredFields.requirePickFive) {
				headers.push({
					text: 'Pick 5',
					value: 'pickFive',
					sortable: false
				});
			}

			headers.push({
				align: 'center',
				value: 'actions',
				sortable: false
			});

			return headers;
		}
	},
	methods: {
		async clearResult(result) {
			Object.keys(result.requiredFields).
				forEach(key => {
					result[key] = null;
				});
		},
		async saveResult({ sortition, requiredFields, fillableFields, ...fields }) {
			const results = [
				{
					sortitionId: sortition.id,
					...fields
				}
			];

			this.$startLoading();
			try {
				await updateResults(this.date, results);

				Object.keys(requiredFields).
					forEach(key => {
						this.$set(this.currentlyEditingResult, key, fields[key]);
					});
				this.currentlyEditingResult = null;
			} catch (e) {
				console.log(e);
			} finally {
				this.$finishedLoading();
			}
		},
		async onDateChange(date) {
			this.$startLoading();
			this.currentlyEditingResult = null;
			await this.getResults(date);
			this.$finishedLoading();
		},
		async getResults(date) {
			try {
				const { data } = await fetchResults(date),
					{ results, date: serverDate, requiredFields } = data;

				if (!date) {
					this.date = serverDate;
				}
				this.results = results;
				this.requiredFields = requiredFields;
			} catch (e) {
				console.error(e);
			}
		}
	}
};
</script>

<style scoped></style>
